import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  ArrowLeft, Loader2, Building, User, Calendar, Tag, Shield, Zap,
  GitBranch, Lightbulb, FolderTree, Map, Video, BookOpen, ChevronRight,
  Clock, FileText, ExternalLink, BarChart3, Sparkles, AlertTriangle,
  TrendingUp, TrendingDown, Target, Activity, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { LEVEL_COLORS, STATUS_COLORS, TYPE_COLORS } from '../components/ProcessTree';
import { cn } from '../lib/utils';

const LEVEL_NAMES = { 1: "Enterprise", 2: "Value Stream", 3: "End-to-End Process", 4: "Sub-Process", 5: "Task" };

const formatDuration = (secs) => {
  if (!secs && secs !== 0) return '-';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const ProcessDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [proc, setProc] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [perfData, setPerfData] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    api.get(`/processes/${id}`).then(r => setProc(r.data)).catch(() => {
      toast.error('Process not found');
      navigate('/processes');
    }).finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    if (activeTab === 'performance' && !perfData) {
      setPerfLoading(true);
      api.get(`/processes/${id}/performance`).then(r => setPerfData(r.data)).catch(() => {}).finally(() => setPerfLoading(false));
    }
  }, [activeTab, id, perfData]);

  const handleRunAI = async () => {
    setAiLoading(true);
    try {
      const r = await api.post(`/processes/${id}/ai-insights`);
      setAiInsights(r.data.insights);
    } catch (err) {
      toast.error('AI analysis failed');
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-[#235894]" /></div>;
  if (!proc) return null;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'artefacts', label: `Linked Artefacts (${proc.linked_counts?.total || 0})` },
    { id: 'performance', label: 'Performance' },
    { id: 'ai-insights', label: 'AI Insights' },
  ];

  const SEVERITY_COLORS = { High: 'text-red-600 bg-red-50', Medium: 'text-amber-600 bg-amber-50', Low: 'text-blue-600 bg-blue-50' };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="process-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            {proc.parent && (
              <div className="flex items-center gap-1 text-sm text-slate-400 mb-1">
                <button className="hover:text-[#235894]" onClick={() => navigate(`/processes/${proc.parent.id}`)}>L{proc.parent.level} {proc.parent.name}</button>
                <ChevronRight className="w-3 h-3" />
              </div>
            )}
            <div className="flex items-center gap-3">
              <span className={cn('w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg', LEVEL_COLORS[proc.level])}>{proc.level}</span>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">{proc.name}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={STATUS_COLORS[proc.status]}>{proc.status}</Badge>
                  <Badge variant="outline" className={TYPE_COLORS[proc.process_type]}>{proc.process_type}</Badge>
                  <span className="text-sm text-slate-400">{LEVEL_NAMES[proc.level]} | v{proc.version}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}><GitBranch className="w-4 h-4 mr-2" /> Back</Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={cn('px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              activeTab === tab.id ? 'border-[#235894] text-[#235894]' : 'border-transparent text-slate-500 hover:text-slate-700'
            )} data-testid={`tab-${tab.id}`}>{tab.label}</button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {proc.description && <Card><CardContent className="pt-5"><p className="text-slate-700">{proc.description}</p></CardContent></Card>}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Building, label: 'Departments', value: proc.departments?.join(', ') || '-' },
              { icon: User, label: 'Process Owner', value: proc.process_owner || '-' },
              { icon: Shield, label: 'Criticality / Risk', value: `${proc.criticality} / ${proc.risk_rating}` },
              { icon: Zap, label: 'Automation Potential', value: proc.automation_potential },
            ].map(({ icon: Icon, label, value }) => (
              <Card key={label}><CardContent className="py-4 px-4">
                <div className="flex items-center gap-3"><Icon className="w-5 h-5 text-slate-400" /><div><p className="text-xs text-slate-400">{label}</p><p className="font-medium text-sm">{value}</p></div></div>
              </CardContent></Card>
            ))}
          </div>
          {proc.tags?.length > 0 && <div className="flex items-center gap-2 flex-wrap"><Tag className="w-4 h-4 text-slate-400" />{proc.tags.map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}</div>}
          {proc.children?.length > 0 && (
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">Child Processes ({proc.children.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {proc.children.map(c => (
                  <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/processes/${c.id}`)}>
                    <span className={cn('w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold', LEVEL_COLORS[c.level])}>{c.level}</span>
                    <div className="flex-1"><p className="font-medium text-sm">{c.name}</p><p className="text-xs text-slate-400">{LEVEL_NAMES[c.level]}</p></div>
                    <Badge className={cn('text-xs', STATUS_COLORS[c.status])}>{c.status}</Badge>
                    <ExternalLink className="w-4 h-4 text-slate-300" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <Calendar className="w-3 h-3" />
            <span>Created {new Date(proc.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} by {proc.created_by_name}</span>
            <span>Modified {new Date(proc.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        </div>
      )}

      {/* Linked Artefacts Tab */}
      {activeTab === 'artefacts' && (
        <div className="space-y-6">
          {[
            { key: 'linked_ideas', label: 'CI Ideas', icon: Lightbulb, color: 'text-amber-500', path: '/ideas', createPath: `/ideas/new?linkProcess=${id}`, createLabel: 'New Idea' },
            { key: 'linked_projects', label: 'CI Projects', icon: FolderTree, color: 'text-blue-500', path: '/projects', createPath: null, createLabel: null },
            { key: 'linked_process_maps', label: 'Process Maps', icon: Map, color: 'text-teal-500', path: '/process-maps', createPath: `/process-maps/new?linkProcess=${id}`, createLabel: 'New Process Map' },
            { key: 'linked_captures', label: 'Process Captures', icon: Video, color: 'text-purple-500', path: '/process-capture', createPath: `/process-capture/new?linkProcess=${id}`, createLabel: 'New Capture' },
            { key: 'linked_sops', label: 'SOPs', icon: BookOpen, color: 'text-rose-500', path: '/sop-generator', createPath: null, createLabel: null },
          ].map(({ key, label, icon: Icon, color, path, createPath, createLabel }) => {
            const items = proc[key] || [];
            return (
              <Card key={key}>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Icon className={cn('w-5 h-5', color)} />{label} ({items.length})</CardTitle>
                  {createPath && (
                    <Button size="sm" variant="outline" onClick={() => navigate(createPath)} className="text-xs">
                      <Plus className="w-3 h-3 mr-1" /> {createLabel}
                    </Button>
                  )}
                </CardHeader>
                <CardContent>{items.length === 0
                  ? <div className="text-center py-4">
                      <p className="text-sm text-slate-400 mb-2">No {label.toLowerCase()} linked</p>
                      {createPath && (
                        <Button size="sm" variant="outline" onClick={() => navigate(createPath)}>
                          <Plus className="w-3 h-3 mr-1" /> Create {createLabel}
                        </Button>
                      )}
                    </div>
                  : <div className="space-y-2">{items.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`${path}/${item.id}`)}>
                      <div><p className="font-medium text-sm">{item.title || item.name}</p>{item.department && <p className="text-xs text-slate-400">{item.department}</p>}</div>
                      <div className="flex items-center gap-2">{item.status && <Badge variant="outline" className="text-xs">{item.status}</Badge>}<ExternalLink className="w-4 h-4 text-slate-300" /></div>
                    </div>
                  ))}</div>
                }</CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="space-y-6" data-testid="performance-tab">
          {perfLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#235894]" /></div>
          ) : perfData ? (
            <>
              {/* Own metrics */}
              {perfData.own_metrics ? (
                <Card>
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="w-5 h-5 text-[#235894]" /> Direct Performance ({perfData.own_metrics.recording_count} recordings)</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                        <TrendingUp className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
                        <p className="text-2xl font-bold text-emerald-700">{formatDuration(perfData.own_metrics.fastest_time)}</p>
                        <p className="text-xs text-slate-500">Fastest</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <Target className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                        <p className="text-2xl font-bold text-blue-700">{formatDuration(perfData.own_metrics.average_time)}</p>
                        <p className="text-xs text-slate-500">Average</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg border border-red-100">
                        <TrendingDown className="w-5 h-5 mx-auto mb-1 text-red-500" />
                        <p className="text-2xl font-bold text-red-700">{formatDuration(perfData.own_metrics.slowest_time)}</p>
                        <p className="text-xs text-slate-500">Slowest</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <BarChart3 className="w-5 h-5 mx-auto mb-1 text-slate-500" />
                        <p className="text-2xl font-bold text-slate-700">{perfData.own_metrics.time_std_dev}s</p>
                        <p className="text-xs text-slate-500">Std Deviation</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-lg font-bold">{perfData.own_metrics.avg_step_count}</p>
                        <p className="text-xs text-slate-500">Avg Steps</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-lg font-bold">{perfData.own_metrics.min_step_count}</p>
                        <p className="text-xs text-slate-500">Min Steps</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-lg font-bold">{perfData.own_metrics.max_step_count}</p>
                        <p className="text-xs text-slate-500">Max Steps</p>
                      </div>
                    </div>
                    {/* Recordings table */}
                    {perfData.own_metrics.recordings?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-600 mb-2">Recordings</h4>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50"><tr>
                              <th className="px-3 py-2 text-left text-xs text-slate-500">Name</th>
                              <th className="px-3 py-2 text-left text-xs text-slate-500">Duration</th>
                              <th className="px-3 py-2 text-left text-xs text-slate-500">Steps</th>
                              <th className="px-3 py-2 text-left text-xs text-slate-500">By</th>
                            </tr></thead>
                            <tbody>{perfData.own_metrics.recordings.map(r => (
                              <tr key={r.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/process-capture/${r.id}`)}>
                                <td className="px-3 py-2 font-medium">{r.name}</td>
                                <td className="px-3 py-2">{formatDuration(r.duration)}</td>
                                <td className="px-3 py-2">{r.step_count}</td>
                                <td className="px-3 py-2 text-slate-400">{r.recorded_by}</td>
                              </tr>
                            ))}</tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card><CardContent className="py-12 text-center">
                  <Activity className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                  <p className="text-slate-500 mb-1">No direct recordings linked to this process</p>
                  <p className="text-xs text-slate-400">Link process captures to see performance data</p>
                </CardContent></Card>
              )}

              {/* Child rollup */}
              {perfData.child_metrics?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#26B8A7]" /> Child Process Rollup</CardTitle>
                    {perfData.rollup_total_time && <CardDescription>Aggregated standard time: {formatDuration(perfData.rollup_total_time)}</CardDescription>}
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50"><tr>
                          <th className="px-3 py-2 text-left text-xs text-slate-500">Process</th>
                          <th className="px-3 py-2 text-left text-xs text-slate-500">Avg Time</th>
                          <th className="px-3 py-2 text-left text-xs text-slate-500">Recordings</th>
                        </tr></thead>
                        <tbody>{perfData.child_metrics.map(c => (
                          <tr key={c.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/processes/${c.id}`)}>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: LEVEL_COLORS[c.level] }}>{c.level}</span>
                                <span className="font-medium">{c.name}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2">{c.average_time ? formatDuration(c.average_time) : '-'}</td>
                            <td className="px-3 py-2">{c.recording_count}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card><CardContent className="py-12 text-center"><p className="text-slate-400">Failed to load performance data</p></CardContent></Card>
          )}
        </div>
      )}

      {/* AI Insights Tab */}
      {activeTab === 'ai-insights' && (
        <div className="space-y-6" data-testid="ai-insights-tab">
          {!aiInsights && !aiLoading && (
            <Card><CardContent className="py-12 text-center">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-[#26B8A7]/40" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">AI Process Intelligence</h3>
              <p className="text-slate-500 text-sm mb-4 max-w-md mx-auto">
                Analyse this process using its hierarchy, linked captures, maps, SOPs, ideas, and projects to identify waste, automation opportunities, and variability.
              </p>
              <Button onClick={handleRunAI} className="bg-gradient-to-r from-[#235894] to-[#26B8A7]" data-testid="run-ai-analysis-btn">
                <Sparkles className="w-4 h-4 mr-2" /> Run AI Analysis
              </Button>
            </CardContent></Card>
          )}

          {aiLoading && (
            <Card><CardContent className="py-16 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#235894] mx-auto mb-4" />
              <p className="text-slate-500">Analysing process data...</p>
            </CardContent></Card>
          )}

          {aiInsights && !aiLoading && (
            <>
              {/* Executive Summary */}
              {aiInsights.executive_summary && (
                <Card className="border-l-4 border-l-[#235894]">
                  <CardContent className="pt-5"><p className="text-slate-700 leading-relaxed">{aiInsights.executive_summary}</p></CardContent>
                </Card>
              )}

              {/* Waste Analysis */}
              {aiInsights.waste_analysis?.length > 0 && (
                <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Waste Analysis</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {aiInsights.waste_analysis.map((w, i) => (
                      <div key={i} className="p-4 rounded-lg border">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={SEVERITY_COLORS[w.severity] || 'bg-slate-50 text-slate-600'}>{w.severity}</Badge>
                          <span className="font-medium text-sm">{w.type}</span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{w.description}</p>
                        <p className="text-sm text-[#235894]"><span className="font-medium">Recommendation:</span> {w.recommendation}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Automation Opportunities */}
              {aiInsights.automation_opportunities?.length > 0 && (
                <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" /> Automation Opportunities</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {aiInsights.automation_opportunities.map((a, i) => (
                      <div key={i} className="p-4 rounded-lg border">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-sm">{a.area}</span>
                          <Badge variant="outline" className="text-xs">Impact: {a.impact}</Badge>
                          <Badge variant="outline" className="text-xs">Effort: {a.effort}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">{a.description}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Variability Insights */}
              {aiInsights.variability_insights?.length > 0 && (
                <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="w-5 h-5 text-purple-500" /> Variability Insights</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {aiInsights.variability_insights.map((v, i) => (
                      <div key={i} className="p-4 rounded-lg border">
                        <p className="text-sm font-medium text-slate-800 mb-1">{v.observation}</p>
                        {v.cause && <p className="text-sm text-slate-500 mb-1"><span className="font-medium">Cause:</span> {v.cause}</p>}
                        <p className="text-sm text-[#235894]"><span className="font-medium">Recommendation:</span> {v.recommendation}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Improvement Suggestions */}
              {aiInsights.improvement_suggestions?.length > 0 && (
                <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-500" /> Improvement Suggestions</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {aiInsights.improvement_suggestions.map((s, i) => (
                      <div key={i} className="flex gap-3 p-4 rounded-lg border">
                        <div className="w-8 h-8 rounded-full bg-[#235894] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">{s.priority || i + 1}</div>
                        <div>
                          <p className="font-medium text-sm">{s.title}</p>
                          <p className="text-sm text-slate-600 mt-1">{s.description}</p>
                          <Badge variant="outline" className="text-xs mt-2">Impact: {s.expected_impact}</Badge>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Linked Improvements */}
              {aiInsights.linked_improvements && (
                <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><Lightbulb className="w-5 h-5 text-amber-500" /> Existing CI Activity</CardTitle></CardHeader>
                  <CardContent><p className="text-sm text-slate-700">{aiInsights.linked_improvements}</p></CardContent>
                </Card>
              )}

              {/* Risk Flags */}
              {aiInsights.risk_flags?.length > 0 && (
                <Card className="border-red-200"><CardHeader><CardTitle className="text-base flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" /> Risk Flags</CardTitle></CardHeader>
                  <CardContent><ul className="space-y-2">{aiInsights.risk_flags.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-red-700"><AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />{r}</li>
                  ))}</ul></CardContent>
                </Card>
              )}

              {/* Re-run button */}
              <div className="text-center">
                <Button variant="outline" onClick={handleRunAI} disabled={aiLoading}>
                  <Sparkles className="w-4 h-4 mr-2" /> Re-run Analysis
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProcessDetail;
